/**
 * AppRoutes - All route definitions for Destiny Junior PWA
 */
import { lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "../components/auth/ProtectedRoute";

// Critical pages loaded eagerly
import LoginPage from "../pages/LoginPage";
import AdminDashboard from "../pages/AdminDashboard";
import GuruDashboard from "../pages/GuruDashboard";
import ParentDashboard from "../pages/ParentDashboard";
import ChildrenPage from "../pages/ChildrenPage";
import ActivitiesPage from "../pages/ActivitiesPage";
import NutritionPage from "../pages/NutritionPage";
import DailyLogsPage from "../pages/DailyLogsPage";

// Lazy loaded pages
const DevelopmentPage = lazy(() => import("../pages/DevelopmentPage"));
const NutritionalStatusPage = lazy(() => import("../pages/NutritionalStatusPage"));
const NutritionalDetailPage = lazy(() => import("../pages/NutritionalDetailPage"));
const ClassesPage = lazy(() => import("../pages/ClassesPage"));
const ManageTeachersPage = lazy(() => import("../pages/ManageTeachersPage"));
const ManageParentsPage = lazy(() => import("../pages/ManageParentsPage"));
const ProfileSettingsPage = lazy(() => import("../pages/ProfileSettingsPage"));
const SuperadminDashboard = lazy(() => import("../pages/SuperadminDashboard"));
const ReportsPage = lazy(() => import("../pages/ReportsPage"));
const BillingPage = lazy(() => import("../pages/BillingPage"));
const ResetPasswordPage = lazy(() => import("../pages/ResetPasswordPage"));
const SetupPasswordPage = lazy(() => import("../pages/SetupPasswordPage"));
const ChildRegistrationPage = lazy(() => import("../pages/ChildRegistrationPage"));
const TakwimPage = lazy(() => import("../pages/TakwimPage"));
const GuruProfileSettingsPage = lazy(() => import("../pages/GuruProfileSettingsPage"));
const GuidePage = lazy(() => import("../pages/GuidePage"));
const GuideEditorPage = lazy(() => import("../pages/GuideEditorPage"));
const AdminGuidePage = lazy(() => import("../pages/AdminGuidePage"));
const AdminGuideEditorPage = lazy(() => import("../pages/AdminGuideEditorPage"));
const TeacherProfilePage = lazy(() => import("../pages/TeacherProfilePage"));
const AdminProfilePage = lazy(() => import("../pages/AdminProfilePage"));
const BranchManagerDashboard = lazy(() => import("../pages/BranchManagerDashboard"));
const AlbumPage = lazy(() => import("../pages/AlbumPage"));
const AlbumManagePage = lazy(() => import("../pages/AlbumManagePage"));
const AuditLogPage = lazy(() => import("../pages/AuditLogPage"));
const ParentPanelPage = lazy(() => import("../pages/ParentPanelPage"));
const PendingApprovalPage = lazy(() => import("../pages/PendingApprovalPage"));
const ChildApprovalDashboard = lazy(() => import("../pages/ChildApprovalDashboard"));
const UserApprovalDashboard = lazy(() => import("../pages/UserApprovalDashboard"));
const ParentGuidePage = lazy(() => import("../pages/ParentGuidePage"));
const ClassTransitionPage = lazy(() => import("../pages/ClassTransitionPage"));
const AttendancePage = lazy(() => import("../pages/AttendancePage"));
const PakejPage = lazy(() => import("../pages/PakejPage"));
const AppInstallStatusPage = lazy(() => import("../pages/AppInstallStatusPage"));
const IssueTicketsPage = lazy(() => import("../pages/IssueTicketsPage"));
const Kids2027Survey = lazy(() => import("../pages/Kids2027Survey"));
const Survey2027ResultsPage = lazy(() => import("../pages/Survey2027ResultsPage"));
const TermReportApprovalPage = lazy(() => import("../pages/TermReportApprovalPage"));
const LandingBuilderPage = lazy(() => import("../pages/LandingBuilderPage"));
const MimiSkillsPage = lazy(() => import("../pages/MimiSkillsPage"));
const MimiSkillWorkspace = lazy(() => import("../pages/MimiSkillWorkspace"));

export default function AppRoutes({ user, onLogin, onLogout, onUserUpdate }) {
  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to="/" /> : <LoginPage onLogin={onLogin} />
      } />
      
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Halaman awam Pakej - senarai pakej sistem */}
      <Route path="/pakej" element={<PakejPage />} />

      {/* Landing Page Builder (Skill Mimi Junior) - Superadmin */}
      <Route path="/mimi/skills" element={user ? <MimiSkillsPage user={user} onLogout={onLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/mimi/skills/:skillId" element={user ? <MimiSkillWorkspace user={user} onLogout={onLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/mimi/landing-builder" element={user ? <LandingBuilderPage user={user} /> : <Navigate to="/login" replace />} />
      <Route path="/mimi/landing-builder/:projectId" element={user ? <LandingBuilderPage user={user} /> : <Navigate to="/login" replace />} />
      <Route path="/mimi/landing-builder/:projectId/preview" element={user ? <LandingBuilderPage user={user} preview /> : <Navigate to="/login" replace />} />

      {/* Halaman AWAM Pengesahan Pelajar - /kidsYYYY ikut tahun sesi (bukan corak kidsYYYY -> laman utama) */}
      <Route path="/:surveySlug" element={<Kids2027Survey />} />

      {/* Dashboard keputusan Pengesahan Pelajar Sesi 2027 - superadmin & branch_manager */}
      <Route path="/survey-2027" element={
        !user ? <Navigate to="/login" /> :
        ['superadmin', 'branch_manager'].includes(user.role) ?
        <Survey2027ResultsPage user={user} onLogout={onLogout} /> :
        <Navigate to="/" />
      } />

      {/* Kelulusan Laporan Penggal - superadmin & branch_manager */}
      <Route path="/laporan-penggal" element={
        !user ? <Navigate to="/login" /> :
        ['superadmin', 'branch_manager'].includes(user.role) ?
        <TermReportApprovalPage user={user} onLogout={onLogout} /> :
        <Navigate to="/" />
      } />

      {/* Tiket Isu pengguna - superadmin sahaja */}
      <Route path="/tiket-isu" element={
        !user ? <Navigate to="/login" /> : user.role === 'superadmin' ? <IssueTicketsPage user={user} onLogout={onLogout} /> : <Navigate to="/" />
      } />

      {/* Status Pemasangan App - superadmin, pengurus & admin */}
      <Route path="/status-pemasangan" element={
        !user ? <Navigate to="/login" /> :
        ['superadmin', 'branch_manager', 'admin'].includes(user.role) ?
        <AppInstallStatusPage user={user} onLogout={onLogout} /> :
        <Navigate to="/" />
      } />

      <Route path="/setup-password" element={<SetupPasswordPage />} />
      
      {/* Dashboard - role-based */}
      <Route path="/" element={
        !user ? <Navigate to="/login" /> : 
        user.role === 'superadmin' ? <SuperadminDashboard user={user} onLogout={onLogout} /> :
        user.role === 'branch_manager' ? <BranchManagerDashboard user={user} onLogout={onLogout} /> :
        user.role === 'admin' ? <AdminDashboard user={user} onLogout={onLogout} /> :
        user.role === 'guru' ? <GuruDashboard user={user} onLogout={onLogout} /> :
        <ParentDashboard user={user} onLogout={onLogout} />
      } />
      
      {/* Protected pages */}
      <Route path="/children" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/children">
          <ChildrenPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/activities" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/activities">
          <ActivitiesPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/nutrition" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/nutrition">
          <NutritionPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/daily-logs" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/daily-logs">
          <DailyLogsPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/development" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/development">
          <DevelopmentPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/nutritional-status" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/nutritional-status">
          <NutritionalStatusPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/nutritional-status/:childId" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/nutritional-status">
          <NutritionalDetailPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/classes" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/classes">
          <ClassesPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      {/* Role-restricted pages */}
      <Route path="/manage-teachers" element={
        !user ? <Navigate to="/login" /> : 
        ['admin', 'superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/manage-teachers">
            <ManageTeachersPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />

      <Route path="/teacher-profile/:teacherId" element={
        !user ? <Navigate to="/login" /> : 
        <TeacherProfilePage user={user} onLogout={onLogout} />
      } />

      <Route path="/admin-profile" element={
        !user ? <Navigate to="/login" /> : 
        <AdminProfilePage user={user} onLogout={onLogout} />
      } />
      
      {/* Album Routes */}
      <Route path="/album" element={
        !user ? <Navigate to="/login" /> : 
        user.role === 'ibu_bapa' ? (
          <ProtectedRoute user={user} path="/album">
            <AlbumPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/album-manage" />
      } />
      
      <Route path="/album-manage" element={
        !user ? <Navigate to="/login" /> : 
        ['admin', 'branch_manager', 'superadmin', 'guru'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/album-manage">
            <AlbumManagePage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/album" />
      } />
      
      <Route path="/panel-ibu-bapa" element={
        !user ? <Navigate to="/login" /> :
        ['superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/panel-ibu-bapa">
            <ParentPanelPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />

      <Route path="/audit-logs" element={
        !user ? <Navigate to="/login" /> : 
        ['superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/audit-logs">
            <AuditLogPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />
      
      <Route path="/pending-approval" element={
        !user ? <Navigate to="/login" /> : 
        ['superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/pending-approval">
            <PendingApprovalPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />
      
      <Route path="/kelulusan-kanak-kanak" element={
        !user ? <Navigate to="/login" /> : 
        ['superadmin', 'branch_manager', 'admin'].includes(user.role) ? 
        <ChildApprovalDashboard user={user} onLogout={onLogout} /> :
        <Navigate to="/" />
      } />
      
      <Route path="/kelulusan-pengguna" element={
        !user ? <Navigate to="/login" /> : 
        ['superadmin', 'branch_manager', 'admin'].includes(user.role) ? 
        <UserApprovalDashboard user={user} onLogout={onLogout} /> :
        <Navigate to="/" />
      } />
      
      <Route path="/class-transition" element={
        !user ? <Navigate to="/login" /> : 
        ['admin', 'superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/class-transition">
            <ClassTransitionPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />
      
      <Route path="/panduan-ibu-bapa" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/panduan-ibu-bapa">
          <ParentGuidePage />
        </ProtectedRoute>
      } />
      
      <Route path="/manage-parents" element={
        !user ? <Navigate to="/login" /> : 
        ['admin', 'superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/manage-parents">
            <ManageParentsPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />

      <Route path="/profile-settings" element={
        !user ? <Navigate to="/login" /> : 
        <ProfileSettingsPage user={user} onLogout={onLogout} />
      } />
      
      <Route path="/settings" element={
        !user ? <Navigate to="/login" /> : 
        user.role === "guru" ? <GuruProfileSettingsPage user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} /> :
        <ProfileSettingsPage user={user} onLogout={onLogout} />
      } />

      <Route path="/reports" element={
        !user ? <Navigate to="/login" /> : 
        ['admin', 'superadmin', 'branch_manager'].includes(user.role) ? (
          <ProtectedRoute user={user} path="/reports">
            <ReportsPage user={user} onLogout={onLogout} />
          </ProtectedRoute>
        ) :
        <Navigate to="/" />
      } />

      <Route path="/attendance" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/attendance">
          <AttendancePage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />

      <Route path="/billing" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/billing">
          <BillingPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />

      <Route path="/child-registration" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/child-registration">
          <ChildRegistrationPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/child-registration/:registrationId" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/child-registration">
          <ChildRegistrationPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />

      <Route path="/pendaftaran-kanak-kanak" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/pendaftaran-kanak-kanak">
          <ChildRegistrationPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/pendaftaran-kanak-kanak/:registrationId" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/pendaftaran-kanak-kanak">
          <ChildRegistrationPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />

      <Route path="/takwim" element={
        !user ? <Navigate to="/login" /> : 
        <ProtectedRoute user={user} path="/takwim">
          <TakwimPage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />

      <Route path="/tetapan-guru" element={
        !user ? <Navigate to="/login" /> : 
        user.role !== "guru" ? <Navigate to="/" /> :
        <GuruProfileSettingsPage user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />
      } />

      {/* Panduan Routes */}
      <Route path="/panduan" element={<GuidePage />} />
      
      <Route path="/panduan-admin" element={
        !user ? <Navigate to="/login" /> :
        !["admin", "superadmin"].includes(user.role) ? <Navigate to="/" /> :
        <ProtectedRoute user={user} path="/panduan-admin">
          <AdminGuidePage user={user} onLogout={onLogout} />
        </ProtectedRoute>
      } />
      
      <Route path="/superadmin/panduan-editor" element={
        !user ? <Navigate to="/login" /> :
        user.role !== "superadmin" ? <Navigate to="/" /> :
        <GuideEditorPage user={user} />
      } />
      
      <Route path="/superadmin/panduan-admin-editor" element={
        !user ? <Navigate to="/login" /> :
        user.role !== "superadmin" ? <Navigate to="/" /> :
        <AdminGuideEditorPage user={user} onLogout={onLogout} />
      } />

      <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
    </Routes>
  );
}
